import { numGrouping } from '@comeon/utility';
import {
	LOTTERY,
	CASH_BONUS,
	FREE_MONEY_BONUS,
	DEPOSIT_BONUS,
	BONUSES,
	FREESPIN,
	FREEBET,
	AFFORDABLE,
	GOLDEN_CHIP
} from '../../constants/index';
import { ItemSt, Pill } from './types';

// See BANJO-1786
// paymentMethodsIds: [],
// paymentMethodIdsStr: '',
/**
 * [paymentMethodsHelper description]
 * @param  {[shop-object]} item [description]
 * @return {[string]}      ['ALL' | '1,2,3,4,5']
 */
const paymentMethodsHelper = (item: ItemSt) => {
	let bonusPaymentMethods = 'ALL';
	if (item.item && item.item.paymentMethodsIds !== undefined && item.item.paymentMethodsIds.length > 0) {
		bonusPaymentMethods = item.item.paymentMethodsIds.join();
	} else if (item.item && item.item.paymentMethodIdsStr) {
		bonusPaymentMethods = item.item.paymentMethodIdsStr;
	}

	return bonusPaymentMethods;
};

/**
 *
 * @param {Number} diffDays
 * @return {Object}
 */
const formatTimeRemaining = (diffDays: number) => {
	if (diffDays >= 1 && diffDays < 2) {
		const timeRemaining = {
			format: 'day',
			remaining: Math.floor(diffDays)
		};
		return timeRemaining;
	}
	if (diffDays >= 2) {
		const timeRemaining = {
			format: 'days',
			remaining: Math.round(diffDays)
		};
		return timeRemaining;
	}

	const milliseconds = diffDays * 24 * 60 * 60 * 1000; // days (>1) * hours * minutes * seconds * milliseconds
	const timeRemaining = {
		format: 'milliseconds',
		remaining: milliseconds
	};
	return timeRemaining;
};

/**
 *
 * @param {Number} nextDrawDate
 * @returns {object} Contains 2 properties, format: 'string' and remaining: number
 */
const lotteryTimeRemainingHelper = (nextDrawDate: number) => {
	const drawDate = new Date(nextDrawDate);
	const now = Date.now();

	if (now > drawDate.getTime()) {
		return { format: 'drawn', remaining: 0 };
	}

	// Calculate how many days there is between the days
	const oneDay = 24 * 60 * 60 * 1000; // hours * minutes * seconds * milliseconds
	const diffDays = Math.abs((now - drawDate.getTime()) / oneDay);
	const timeRemaining = formatTimeRemaining(diffDays); // Helper-function to format what to return. Day / Days / Milliseconds
	return timeRemaining;
};

/**
 * Checks if the item is a "bonus".
 * In this specific case we count both FreeMoneyBonus and DepositBonus
 * as "Bonus".
 * @param {itemType} itemType is what we use to filter items on. Eg. shopItems.item.itemType.
 * @return {boolean} Returns true if argument is a bonus of type Deposit or FreeMoney.
 */
function isBonus(itemType: string) {
	if (itemType === FREE_MONEY_BONUS) {
		return true;
	}
	if (itemType === DEPOSIT_BONUS) {
		return true;
	}
	return false;
}

type Test = {
	label: string;
	instances: number;
};

/**
 * createFilterPillsHelper is a function that figures out which filters are going into the <Pills /> component,
 * which is used for filtering items - in this case in the shop.
 *
 * Note that in this case we use each items "type" to filter on. However, it was decided that FreeMoneyBounus
 * and DepositBonus should be merged together and be treated as one type.
 *
 * @param {Array} shopItems The list of all available shop-items
 * @param {String} SHOP_FILTER_PILL_AFFORDABLE Textkey for the "Affordable"-label
 * @param {String} SHOP_FILTER_PILL_BONUSES Textkey for the "Bonuses"-label
 *
 * See BANJO-9584 for further details.
 */
function createFilterPillsHelper(
	shopItems: ItemSt[],
	points: number,
	SHOP_FILTER_PILL_AFFORDABLE: string,
	SHOP_FILTER_PILL_BONUSES: string
) {
	if (shopItems.length < 1) {
		return [];
	}
	const pills: Pill[] = [];
	const pillsOrder = [AFFORDABLE, FREESPIN, FREEBET, BONUSES, CASH_BONUS, LOTTERY, GOLDEN_CHIP];
	/* Count how many instances there are of each category.
		DepositBonuses and FreeMoneyBonuses are treated as the same. */
	const initialValue = {};
	const countedTypes = shopItems.reduce((accumulator: { [key: string]: Test }, currentShopItem) => {
		if (currentShopItem.type in accumulator) {
			accumulator[currentShopItem.type].instances += 1;
		} else if (isBonus(currentShopItem.type) && !accumulator[BONUSES]) {
			/* If we hit an itemType that is either a DepositBonus or FreeMoneyBonus,
			create a new combined type for those, called "Bonuses". */
			accumulator[BONUSES] = {
				label: SHOP_FILTER_PILL_BONUSES,
				instances: 1
			};
		} else if (isBonus(currentShopItem.type) && accumulator[BONUSES]) {
			accumulator[BONUSES].instances += 1;
		} else {
			accumulator[currentShopItem.type] = {
				instances: 1,
				label: currentShopItem.typeText
			};
		}
		return accumulator;
	}, initialValue);

	// Does not display any filters if there is only one item type and no items are affordable
	if (Object.keys(countedTypes).length === 1) {
		const areAllItemsUnaffordable = shopItems.every(item => item.playerPointsCost >= points);
		if (areAllItemsUnaffordable) {
			return [];
		}
	}
	/* Create a pill for each category, but only if there is more than 1 instance of it.
	Affordable is always going to be included */
	pills.push({ label: SHOP_FILTER_PILL_AFFORDABLE, href: AFFORDABLE });
	Object.entries(countedTypes).forEach(([key, value]) => pills.push({ label: value.label, href: key }));

	// Sort the pills in the specific order decided above
	pills.sort((a, b) => {
		const order = pillsOrder.indexOf(a.href) - pillsOrder.indexOf(b.href);
		if (order === 0) {
			// They have the same href, which should never happen but just in case...
			return a.href === b.href ? 0 : a.href < b.href ? -1 : 1;
		}
		return order;
	});

	return pills;
}

function shopItemCostHelper(price: number) {
	if (price >= 0) {
		const priceAsString = price.toString();
		const formattedPrice = numGrouping(priceAsString, false);
		return formattedPrice;
	}
	return '0';
}
const inactiveUser = 'inactive';
const normalLowUser = 'normal low';

const parseStringToObject = (string: string) => {
	const keyValuePairs = string.split('|');
	const object: { [key: string]: string[] } = {};
	keyValuePairs.forEach(pair => {
		if (!pair) {
			return;
		}
		const [key, value] = pair.split(':').map(item => item.trim());
		if (!key || !value) {
			return;
		}
		object[key] = value.split(',').map(item => item.trim().replace(/_/g, ' '));
	});
	return object;
};

const tierLevelByUserSegment = (levelsGuide: { [key: string]: string[] }, userSegment: string) => {
	if (userSegment === 'failure') return userSegment;
	let userSegmentLowerCase = userSegment?.toLowerCase();
	if (userSegmentLowerCase === inactiveUser) {
		userSegmentLowerCase = normalLowUser;
	}
	const tierLevel = Object.keys(levelsGuide).find(key => {
		const levels = levelsGuide[key];
		return levels.find(level => level.toLowerCase() === userSegmentLowerCase);
	});
	return tierLevel;
};

const playerLevelToType = (levelsGuide: { [key: string]: string[] }, userSegment: string) => {
	if (userSegment === 'failure') return 'SHOP_INFO_BANNER_IMG';
	if (!levelsGuide || !userSegment) return null;
	let userSegmentLowerCase = userSegment?.toLowerCase();
	if (userSegmentLowerCase === inactiveUser) {
		userSegmentLowerCase = normalLowUser;
	}
	const values = Object.values(levelsGuide);
	const index = values.findIndex(levelArray =>
		levelArray.find(level => level.toLowerCase() === userSegmentLowerCase)
	);
	let textKeyLevel;
	switch (index) {
		case 0:
			textKeyLevel = 'BRONZE';
			break;
		case 1:
			textKeyLevel = 'SILVER';
			break;
		case 2:
			textKeyLevel = 'GOLD';
			break;
		case 3:
			textKeyLevel = 'PLATINUM';
			break;
		case 4:
			textKeyLevel = 'DIAMOND';
			break;
		case 5:
			textKeyLevel = 'DIAMOND_PLUS';
			break;
		default:
			return null;
	}

	return textKeyLevel;
};

const playerLevelToTextKey = (levelsGuide: { [key: string]: string[] }, userSegment: string) => {
	// if (userSegment === 'failure') return 'SHOP_INFO_BANNER_IMG';
	// if (!levelsGuide || !userSegment) return null;
	// let userSegmentLowerCase = userSegment?.toLowerCase();
	// if (userSegmentLowerCase === inactiveUser) {
	// 	userSegmentLowerCase = normalLowUser;
	// }
	// const values = Object.values(levelsGuide);
	// const index = values.findIndex(levelArray =>
	// 	levelArray.find(level => level.toLowerCase() === userSegmentLowerCase)
	// );
	// let textKeyLevel;
	// switch (index) {
	// 	case 0:
	// 		textKeyLevel = 'BRONZE';
	// 		break;
	// 	case 1:
	// 		textKeyLevel = 'SILVER';
	// 		break;
	// 	case 2:
	// 		textKeyLevel = 'GOLD';
	// 		break;
	// 	case 3:
	// 		textKeyLevel = 'PLATINUM';
	// 		break;
	// 	case 4:
	// 		textKeyLevel = 'DIAMOND';
	// 		break;
	// 	case 5:
	// 		textKeyLevel = 'DIAMOND_PLUS';
	// 		break;
	// 	default:
	// 		return null;
	// }
	const textKeyLevel = playerLevelToType(levelsGuide, userSegment) as string;

	return `SHOP_INFO_BANNER_IMG_${textKeyLevel}`;
};

export {
	paymentMethodsHelper,
	lotteryTimeRemainingHelper,
	createFilterPillsHelper,
	isBonus,
	shopItemCostHelper,
	parseStringToObject,
	tierLevelByUserSegment,
	playerLevelToTextKey,
	playerLevelToType
};
